import { useCallback, useEffect, useRef, useState } from 'react';
import {
  setReturnMethodsResponse,
  setRosTransactionId,
  setSelectedItems,
  useReturnStore,
} from '@src/stores/returnStore';
import { ReturnMethodsAPI } from '@src/api/returns';
import {
  buildReturnMethodsRequest,
  checkReturnMethodsFraudType,
  getReturnMethodsFraudMessage,
  hasItemWithNoReturnMethod,
} from '@src/utils';
import {
  adaptReturnMethodsResponse,
  applyReturnMethodsToItems,
  AdaptedReturnMethods,
} from '@src/utils/eligibility-utils';
import { ReturnFraudType } from '@src/constants/analytics';
import { TagOnApprissDeniedOrWarned } from '@src/utils/analytics-tagging';
import { emitReturnsBusinessError } from '@src/utils/returns-instrumentation-context';

export interface ReturnMethodsFraudOutcome {
  type: ReturnFraudType;
  message: string;
}

/**
 * Why the last attempt resolved the way it did.
 *
 * `fetchReturnMethods` resolves undefined for three unrelated reasons — the
 * call failed, the call succeeded and the customer was warned or declined, and
 * the attempt was superseded — and a caller sequencing further calls behind it
 * has to tell them apart. Reporting a fraud decision as a technical error is
 * the mistake this exists to prevent: the customer would be told something
 * broke when in fact they were answered.
 */
export type ReturnMethodsAttemptOutcome =
  | 'ok'
  | 'failed'
  | 'fraud'
  | 'superseded';

/**
 * Fetches the return methods for the selected items and folds the answer into
 * the eligibilities the method screens read, so the answer exists before that
 * screen renders.
 *
 * Deliberately does NOT touch newFlowStore.chosenReturnMethods: that holds the
 * method the user CHOSE and selects which step sequence they walk, so writing
 * availability into it here would route them before they have chosen.
 *
 * Failure is recoverable and non-advancing — the callback resolves to undefined
 * and the caller stays on the step. Only network and generic failures are
 * reachable today, and both are already captured by the shared request
 * instrumentation, so nothing is emitted here.
 *
 * An answer that is no longer the current one is dropped rather than applied.
 * The step's Back control stays live while a request is in flight, so the
 * customer can leave mid-request; without this the response would land after
 * they had gone, rewrite the selection they were editing, and send them
 * forward from wherever they had got to.
 *
 * The fraud outcome rides on the same answer, so it is read here rather than by
 * the caller. What it does NOT do is decide what the customer sees: the outcome
 * is handed back and the step renders it, so the prompt lives and dies with the
 * step rather than with the page.
 */
export function useReturnMethods() {
  const [isReturnMethodsLoading, setIsReturnMethodsLoading] = useState(false);
  const [hasReturnMethodsError, setHasReturnMethodsError] = useState(false);
  const [fraudOutcome, setFraudOutcome] = useState<ReturnMethodsFraudOutcome>();
  // Two different jobs, both needed. isInFlight refuses to START a second
  // request; currentAttempt decides whether an answer already on its way back
  // is still the one being waited for. The first cannot cover unmount, and the
  // second cannot stop a duplicate request going out.
  //
  // Neither counts attempts. There is no cap on how many times the customer may
  // ask: a failure here is transient and pressing Continue again is the whole
  // of the remedy, so a limit would only take that remedy away.
  const isInFlight = useRef(false);
  const currentAttempt = useRef(0);
  // Held in a ref rather than state, and deliberately so: a caller chaining
  // further calls behind this one reads it in the same tick the promise
  // resolves, before React has re-rendered, so the state above would still be
  // reporting the previous attempt. Same reason the store is read through
  // getState() on the paths that mutate and read within one tick.
  const lastOutcome = useRef<ReturnMethodsAttemptOutcome>('ok');
  useEffect(
    () => () => {
      currentAttempt.current += 1;
    },
    [],
  );

  /**
   * Why the last attempt resolved as it did. Read it straight after awaiting
   * `fetchReturnMethods`, never later — it describes one attempt and the next
   * one overwrites it.
   *
   * Stable across renders on purpose: a caller that sequences further calls
   * behind the fetch keeps this in its own dependency list, and an accessor
   * rebuilt every render would rebuild that caller's callback with it.
   */
  const getLastAttemptOutcome = useCallback(() => lastOutcome.current, []);

  const fetchReturnMethods = useCallback(async (): Promise<
    AdaptedReturnMethods | undefined
  > => {
    // An explicit guard rather than relying on the step disabling its control.
    // The disabled button is presentation; this is the thing that actually
    // stops a second request, and it holds however the call is reached.
    if (isInFlight.current) {
      lastOutcome.current = 'superseded';
      return undefined;
    }
    // Read at call time rather than from a render closure: the quantity step
    // rewrites selectedItems on every quantity and reason edit, and this runs
    // in the same tick as the Continue click.
    const {
      orderDetailsV2,
      returnEligibilityDetails,
      selectedItems,
      isMilitaryExchangeGuest,
      militaryExchange,
    } = useReturnStore.getState();

    currentAttempt.current += 1;
    const attempt = currentAttempt.current;
    // Both cleared up front so this attempt starts from nothing: the customer
    // may have gone back and changed the selection, and the previous answer
    // says nothing about the one being asked for now.
    setHasReturnMethodsError(false);
    setFraudOutcome(undefined);
    setIsReturnMethodsLoading(true);
    isInFlight.current = true;
    try {
      if (!orderDetailsV2) throw new Error('No Order Details');
      if (!returnEligibilityDetails)
        throw new Error('No Return Eligibility Response');

      const request = buildReturnMethodsRequest(
        orderDetailsV2,
        isMilitaryExchangeGuest,
        militaryExchange,
        selectedItems,
        returnEligibilityDetails.et,
      );

      const response = await ReturnMethodsAPI(request);
      // Checked before anything is read or written, so a superseded attempt
      // cannot raise an error either — the attempt that replaced it owns what
      // the customer sees.
      if (currentAttempt.current !== attempt) {
        lastOutcome.current = 'superseded';
        return undefined;
      }
      const returnMethods = response.data;
      if (!returnMethods) {
        throw new Error('ReturnMethodsAPI: Empty 2xx Response');
      }
      // A populated errors[] alongside a 2xx is treated as unusable rather than
      // partial, matching how the v2 eligibility fetch reads the same field.
      if (returnMethods.errors?.length) {
        throw new Error('ReturnMethodsAPI: Response carried errors');
      }
      const answeredLines = new Set(
        returnMethods.lineItems.map((line) => line.lineItemId),
      );
      // A repeated line collapses in the set, so the coverage check below would
      // pass on a response that answers one line twice and another not at all.
      // The adapter keeps the last entry, so the earlier one's methods vanish
      // without trace — count first.
      if (answeredLines.size !== returnMethods.lineItems.length) {
        throw new Error('ReturnMethodsAPI: Response repeated a line');
      }

      // Read once, here, and both check and apply against the same list. The
      // customer can edit while the request is in flight, so the selection the
      // request was built from is not necessarily the one the answer is about
      // to be folded into — checking the first and writing to the second would
      // leave a line added mid-request silently marked unavailable, which is
      // the exact outcome this check exists to prevent.
      const itemsToApply = useReturnStore.getState().selectedItems;
      // An answer that skips a line is unusable. Folding it in would set that
      // line's methods to unavailable, which is indistinguishable from the
      // service deliberately offering it nothing — so a short list would reach
      // the customer as though it were the whole of what is available. An
      // incomplete answer is a failure to retry, never a shorter set of options.
      if (itemsToApply.some((item) => !answeredLines.has(item.lineItemId))) {
        throw new Error('ReturnMethodsAPI: Response missed a selected item');
      }

      const adaptedMethods = adaptReturnMethodsResponse(returnMethods);
      setReturnMethodsResponse(returnMethods);
      // The order-level id the fraud check ran under, held here so the Appriss
      // report can correlate to it at finalize. Written on every answer, not
      // only when one is present: a later answer without a decision means the
      // fraud source was down for this attempt, and holding the previous id
      // would report the return against a check that did not run for it.
      setRosTransactionId(returnMethods.verifyDecision?.transactionId);
      const appliedItems = applyReturnMethodsToItems(
        itemsToApply,
        adaptedMethods,
      );
      setSelectedItems(appliedItems);

      // Read against the items the answer has just been applied to, so the
      // warn-with-nothing-on-offer case sees the same picture the customer is
      // about to be shown.
      const decision = returnMethods.verifyDecision;
      let fraudType = checkReturnMethodsFraudType(decision);
      if (fraudType === 'WARNED' && hasItemWithNoReturnMethod(appliedItems)) {
        fraudType = 'WARNEDINVALID';
      }
      if (fraudType) {
        setFraudOutcome({
          type: fraudType,
          message: getReturnMethodsFraudMessage(decision),
        });
        TagOnApprissDeniedOrWarned(fraudType);
        emitReturnsBusinessError(String(fraudType), {
          source: 'fraud_decision',
        });
        // Resolving undefined holds the step, same as a failure does. A warning
        // is not a refusal, but it still has to be accepted before the customer
        // moves on, and accepting it is the modal's job rather than this one's.
        lastOutcome.current = 'fraud';
        return undefined;
      }
      lastOutcome.current = 'ok';
      return adaptedMethods;
    } catch {
      // Same ownership check the success path makes, and needed for the same
      // reason: a rejection belonging to an abandoned attempt must not speak
      // for the step. The clear below writes to the store rather than to this
      // hook's state, so unmounting does not make it harmless — an attempt the
      // customer walked away from could otherwise wipe the reference a later,
      // successful one had already stored.
      //
      // The pre-flight throws reach here before any await, so the attempt has
      // not moved and this passes; the throws after the response are all
      // downstream of the check above, so the attempt is current by
      // construction.
      if (currentAttempt.current !== attempt) {
        lastOutcome.current = 'superseded';
        return undefined;
      }
      lastOutcome.current = 'failed';
      setHasReturnMethodsError(true);
      // A failed attempt must not leave the previous one's fraud reference
      // standing. Nothing can read it from here — the step holds — but if it
      // survived to a later success it would be overwritten anyway, and if it
      // survived to finalize it would report the return against a check that
      // ran for a different attempt.
      setRosTransactionId(undefined);
      return undefined;
    } finally {
      isInFlight.current = false;
      // Only the live attempt owns the spinner: a superseded one clearing it
      // would leave the step idle while a later request is still running.
      if (currentAttempt.current === attempt) setIsReturnMethodsLoading(false);
    }
  }, []);

  return {
    fetchReturnMethods,
    getLastAttemptOutcome,
    isReturnMethodsLoading,
    hasReturnMethodsError,
    clearReturnMethodsError: () => setHasReturnMethodsError(false),
    fraudOutcome,
    clearFraudOutcome: () => setFraudOutcome(undefined),
  };
}
